import {
  AlertTriangle,
  Bell,
  CalendarCheck2,
  CreditCard,
  MessageSquareText,
  type LucideIcon,
} from "lucide-react";
import type { TFunction } from "i18next";
import type {
  KnownNotificationEvent,
  NotificationEntity,
  NotificationEvent,
} from "@/types/notifications";
import type { UserProfile } from "@/services/authService";
import { isPlatformAdminRole } from "@/utils/accessControl";

const knownNotificationEvents = new Set<KnownNotificationEvent>([
  "BOOKING_CHECKIN_COMPLETED",
  "BOOKING_CHECKOUT_UPCOMING",
  "BOOKING_CHECKOUT_COMPLETED",
  "TASK_CREATED",
  "TASK_STATUS_UPDATED",
  "TASK_ASSIGNED",
  "TASK_TIMEOUT",
  "NEW_CHAT_MESSAGE",
  "TRANSACTION_FAILED",
  "PMS_SYNC_FAILED",
  "SUBSCRIPTION_RENEWS_SOON",
  "SUBSCRIPTION_ENDS_SOON",
  "SUBSCRIPTION_PAYMENT_ISSUE",
  "SUBSCRIPTION_CANCELED",
]);
const requestNotificationEvents = new Set<KnownNotificationEvent>([
  "TASK_CREATED",
  "TASK_STATUS_UPDATED",
  "TASK_ASSIGNED",
  "TASK_TIMEOUT",
]);

type NotificationTone = "default" | "destructive";

export interface NotificationPresentation {
  icon: LucideIcon;
  iconClassName: string;
  titleClassName: string;
  unreadDotClassName: string;
  toastTone: NotificationTone;
}

const genericNotificationRoutes = new Set([
  "/arrivals",
  "/departures",
  "/in-house",
  "/reservations",
  "/requests",
  "/chats",
  "/subscriptions",
]);

export function getNotificationNavigationTarget(
  notification: Pick<
    NotificationEntity,
    "reference" | "metadata" | "bookingId"
  >,
  userProfile?: UserProfile | null,
): string | null {
  const fallbackTarget = getNotificationFallbackTarget(
    notification,
    userProfile,
  );
  const normalizedReference = normalizeNotificationReference(
    notification.reference,
  );

  if (!normalizedReference) {
    return fallbackTarget;
  }

  if (
    fallbackTarget &&
    shouldUseContextualBookingTarget(notification, normalizedReference)
  ) {
    return fallbackTarget;
  }

  if (fallbackTarget && isGenericNotificationRoute(normalizedReference)) {
    return fallbackTarget;
  }

  return normalizedReference;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : null;
}

function getFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const parsedValue = Number(value.trim());

  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizeNotificationReference(
  reference: string | null | undefined,
): string | null {
  const value = getString(reference);

  if (!value) {
    return null;
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      return rewriteLegacyNotificationPath(
        `${url.pathname}${url.search}${url.hash}`,
      );
    } catch {
      return null;
    }
  }

  if (value.startsWith("/")) {
    return rewriteLegacyNotificationPath(value);
  }

  if (value.startsWith("?") || value.startsWith("#")) {
    return null;
  }

  if (value.includes("/")) {
    return rewriteLegacyNotificationPath(`/${value}`);
  }

  return null;
}

function isGenericNotificationRoute(path: string): boolean {
  const [pathname] = path.split(/[?#]/, 1);
  let normalizedPathname = pathname;

  while (normalizedPathname.length > 1 && normalizedPathname.endsWith("/")) {
    normalizedPathname = normalizedPathname.slice(0, -1);
  }

  return genericNotificationRoutes.has(normalizedPathname);
}

function isBookingDetailRoute(path: string): boolean {
  const [pathname] = path.split(/[?#]/, 1);
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length !== 2) {
    return false;
  }

  return (
    (segments[0] === "bookings" || segments[0] === "reservations") &&
    Boolean(segments[1])
  );
}

function shouldUseContextualBookingTarget(
  notification: Pick<NotificationEntity, "metadata" | "bookingId">,
  normalizedReference: string,
): boolean {
  const metadata = notification.metadata;

  if (!isRecord(metadata) || !isKnownNotificationEvent(metadata.event)) {
    return false;
  }

  if (
    metadata.event !== "BOOKING_CHECKIN_COMPLETED" &&
    metadata.event !== "BOOKING_CHECKOUT_UPCOMING"
  ) {
    return false;
  }

  const bookingId =
    getString(notification.bookingId) ?? getMetadataBookingId(metadata);

  return Boolean(bookingId) && isBookingDetailRoute(normalizedReference);
}

function rewriteLegacyNotificationPath(path: string): string {
  if (/^\/admin\/subscriptions\/tenants(?:\/|$)/.test(path)) {
    return "/subscriptions";
  }

  if (path === "/settings/billing") {
    return "/subscriptions";
  }

  if (/^\/payments\/transactions(?:\/|$)/.test(path)) {
    return "/reservations";
  }

  if (/^\/pms\/sync\/jobs(?:\/|$)/.test(path)) {
    return "/pms-integration?tab=sync";
  }

  if (/^\/staff-schedule\/day-off(?:\/|$)/.test(path)) {
    return "/staff-schedule";
  }

  return path.replace(/^\/bookings(?=\/|$)/, "/reservations");
}

function buildBookingTarget(
  basePath: string,
  bookingId: string | null,
  suffix = "",
): string | null {
  if (!bookingId) {
    return null;
  }

  return `${basePath}/${encodeURIComponent(bookingId)}${suffix}`;
}

function getMetadataBookingId(metadata: unknown): string | null {
  if (!isRecord(metadata)) {
    return null;
  }

  return getString(metadata.bookingId);
}

function getNotificationFallbackTarget(
  notification: Pick<NotificationEntity, "metadata" | "bookingId">,
  userProfile?: UserProfile | null,
): string | null {
  const bookingId =
    getString(notification.bookingId) ??
    getMetadataBookingId(notification.metadata);
  const metadata = notification.metadata;

  if (!isRecord(metadata) || !isKnownNotificationEvent(metadata.event)) {
    return buildBookingTarget("/reservations", bookingId);
  }

  switch (metadata.event) {
    case "BOOKING_CHECKIN_COMPLETED":
      return buildBookingTarget("/in-house", bookingId);
    case "BOOKING_CHECKOUT_UPCOMING":
      return buildBookingTarget("/departures", bookingId);
    case "BOOKING_CHECKOUT_COMPLETED":
      return buildBookingTarget("/reservations", bookingId);
    case "TASK_CREATED":
    case "TASK_STATUS_UPDATED":
    case "TASK_ASSIGNED":
    case "TASK_TIMEOUT": {
      const requestId = getString(metadata.requestId);
      return requestId ? `/requests/${encodeURIComponent(requestId)}` : null;
    }
    case "NEW_CHAT_MESSAGE":
      return "/chats";
    case "TRANSACTION_FAILED":
      return buildBookingTarget("/reservations", bookingId, "?tab=payments");
    case "SUBSCRIPTION_RENEWS_SOON":
    case "SUBSCRIPTION_ENDS_SOON":
      return isPlatformAdminRole(userProfile ?? null)
        ? "/subscriptions"
        : "/billing-subscriptions";
    case "SUBSCRIPTION_PAYMENT_ISSUE":
    case "SUBSCRIPTION_CANCELED":
      return isPlatformAdminRole(userProfile ?? null)
        ? "/subscriptions"
        : "/subscription-payments";
    case "PMS_SYNC_FAILED":
      return "/pms-integration?tab=sync";
    default:
      return buildBookingTarget("/reservations", bookingId);
  }
}

export function isKnownNotificationEvent(
  value: unknown,
): value is KnownNotificationEvent {
  return (
    typeof value === "string" &&
    knownNotificationEvents.has(value as KnownNotificationEvent)
  );
}

export function deriveNotificationEvent(
  notification: Pick<NotificationEntity, "metadata">,
): NotificationEvent {
  const metadata = notification.metadata;
  if (!isRecord(metadata)) {
    return "UNKNOWN";
  }

  return isKnownNotificationEvent(metadata.event) ? metadata.event : "UNKNOWN";
}

export function isNotificationVisible(
  notification: Pick<NotificationEntity, "metadata">,
  canUseRequestsSystem: boolean,
): boolean {
  const event = deriveNotificationEvent(notification);

  if (requestNotificationEvents.has(event as KnownNotificationEvent)) {
    return canUseRequestsSystem;
  }

  return true;
}

function formatNotificationDate(value: string): string {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsedDate);
}

function formatNotificationMoney(
  amountValue: unknown,
  currencyValue: unknown,
): string | null {
  const amount = getFiniteNumber(amountValue);
  const currency = getString(currencyValue)?.toUpperCase();

  if (amount === null || !currency) {
    return null;
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function getSubscriptionNotificationSubject(
  metadata: Record<string, unknown>,
  t: TFunction,
): string {
  const hotelName =
    getString(metadata.hotelName) ??
    getString(metadata.hotelTitle) ??
    getString(metadata.propertyName);
  const tenantName = getString(metadata.tenantName);
  const planTitle = getString(metadata.planTitle);
  const subjectParts = [hotelName ?? tenantName, planTitle].filter(Boolean);

  if (subjectParts.length > 0) {
    return subjectParts.join(" · ");
  }

  return t("notifications.subscriptionFallbackSubject", {
    defaultValue: "Subscription",
  });
}

function getSubscriptionNotificationDate(
  metadata: Record<string, unknown>,
): string | null {
  const rawDate =
    getString(metadata.nextPaymentDate) ??
    getString(metadata.nextChargeDate) ??
    getString(metadata.periodEnd);

  return rawDate ? formatNotificationDate(rawDate) : null;
}

function getSubscriptionNotificationAmount(
  metadata: Record<string, unknown>,
): string | null {
  return formatNotificationMoney(
    metadata.nextPaymentAmount ?? metadata.nextChargeAmount ?? metadata.amount,
    metadata.nextPaymentCurrency ??
      metadata.nextChargeCurrency ??
      metadata.currency,
  );
}

function getSubscriptionNotificationBody(
  notification: Pick<NotificationEntity, "metadata">,
  t: TFunction,
): string | null {
  const metadata = notification.metadata;

  if (!isRecord(metadata) || !isKnownNotificationEvent(metadata.event)) {
    return null;
  }

  const subject = getSubscriptionNotificationSubject(metadata, t);
  const chargeDate = getSubscriptionNotificationDate(metadata);
  const amount = getSubscriptionNotificationAmount(metadata);
  const failureMessage = getString(metadata.failureMessage);
  const status =
    getString(metadata.status) ??
    t("notifications.statusUnknown", { defaultValue: "unknown" });

  switch (metadata.event) {
    case "SUBSCRIPTION_RENEWS_SOON":
      if (chargeDate && amount) {
        return t("notifications.subscriptionRenewsSoonBody", {
          subject,
          chargeDate,
          amount,
          defaultValue: "{{subject}} renews on {{chargeDate}} for {{amount}}.",
        });
      }

      if (chargeDate) {
        return t("notifications.subscriptionRenewsSoonBody", {
          subject,
          chargeDate,
          defaultValue: "{{subject}} renews on {{chargeDate}}.",
        });
      }

      if (amount) {
        return t("notifications.subscriptionRenewsSoonBody", {
          subject,
          amount,
          defaultValue: "{{subject}} renews soon. Next charge: {{amount}}.",
        });
      }

      return t("notifications.subscriptionRenewsSoonBody", {
        subject,
        defaultValue: "{{subject}} renews soon.",
      });
    case "SUBSCRIPTION_ENDS_SOON":
      if (chargeDate) {
        return t("notifications.subscriptionEndsSoonBody", {
          subject,
          chargeDate,
          defaultValue: "{{subject}} ends on {{chargeDate}}.",
        });
      }

      return t("notifications.subscriptionEndsSoonBody", {
        subject,
        defaultValue: "{{subject}} ends soon.",
      });
    case "SUBSCRIPTION_PAYMENT_ISSUE":
      if (failureMessage) {
        return t("notifications.subscriptionPaymentIssueBody", {
          subject,
          failureMessage,
          defaultValue: "{{subject}} has a payment issue: {{failureMessage}}.",
        });
      }

      return t("notifications.subscriptionPaymentIssueBody", {
        subject,
        status,
        defaultValue: "{{subject}} has a payment issue ({{status}}).",
      });
    case "SUBSCRIPTION_CANCELED":
      if (failureMessage) {
        return t("notifications.subscriptionCanceledBody", {
          subject,
          failureMessage,
          defaultValue: "{{subject}} was canceled: {{failureMessage}}.",
        });
      }

      return t("notifications.subscriptionCanceledBody", {
        subject,
        status,
        defaultValue: "{{subject}} was canceled ({{status}}).",
      });
    default:
      return null;
  }
}

function getNewChatMessageNotificationBody(
  notification: Pick<NotificationEntity, "metadata">,
  t: TFunction,
): string | null {
  const metadata = notification.metadata;

  if (!isRecord(metadata) || metadata.event !== "NEW_CHAT_MESSAGE") {
    return null;
  }

  const senderName = getString(metadata.senderName);
  const messagePreview = getString(metadata.messagePreview);

  if (senderName && messagePreview) {
    return t("notifications.newChatMessageBody", {
      senderName,
      messagePreview,
      defaultValue: "{{senderName}}: {{messagePreview}}",
    });
  }

  if (messagePreview) {
    return messagePreview;
  }

  return null;
}

export function getNotificationDisplayBody(
  input:
    | string
    | null
    | undefined
    | Pick<NotificationEntity, "body" | "metadata">,
  t: TFunction,
): string {
  const notification =
    typeof input === "object" && input !== null && "body" in input
      ? input
      : null;
  const body = typeof input === "string" ? input : notification?.body;
  const value = body ?? "";
  const statusWithoutPreviousMatch = value.match(
    /^Status changed from\s+to\s+"([^"]+)"\.$/,
  );

  if (statusWithoutPreviousMatch) {
    return t("notifications.statusChangedTo", {
      status: statusWithoutPreviousMatch[1],
    });
  }

  if (notification) {
    const subscriptionBody = getSubscriptionNotificationBody(notification, t);

    if (subscriptionBody) {
      return subscriptionBody;
    }

    const newChatMessageBody = getNewChatMessageNotificationBody(
      notification,
      t,
    );

    if (newChatMessageBody) {
      return newChatMessageBody;
    }
  }

  return value;
}

export function getNotificationPresentation(
  notification: Pick<NotificationEntity, "type" | "priority" | "metadata">,
): NotificationPresentation {
  const event = deriveNotificationEvent(notification);

  switch (event) {
    case "BOOKING_CHECKIN_COMPLETED":
    case "BOOKING_CHECKOUT_UPCOMING":
    case "BOOKING_CHECKOUT_COMPLETED":
      return {
        icon: CalendarCheck2,
        iconClassName: "text-foreground",
        titleClassName: "text-foreground",
        unreadDotClassName: "bg-primary",
        toastTone: "default",
      };
    case "TASK_CREATED":
    case "TASK_STATUS_UPDATED":
    case "TASK_ASSIGNED":
      return {
        icon: Bell,
        iconClassName: "text-foreground",
        titleClassName: "text-foreground",
        unreadDotClassName: "bg-primary",
        toastTone: "default",
      };
    case "TASK_TIMEOUT":
    case "TRANSACTION_FAILED":
    case "PMS_SYNC_FAILED":
    case "SUBSCRIPTION_PAYMENT_ISSUE":
    case "SUBSCRIPTION_CANCELED":
      return {
        icon: AlertTriangle,
        iconClassName: "text-destructive",
        titleClassName: "text-destructive",
        unreadDotClassName: "bg-destructive",
        toastTone: "destructive",
      };
    case "NEW_CHAT_MESSAGE":
      return {
        icon: MessageSquareText,
        iconClassName: "text-foreground",
        titleClassName: "text-foreground",
        unreadDotClassName: "bg-primary",
        toastTone: "default",
      };
    case "SUBSCRIPTION_RENEWS_SOON":
    case "SUBSCRIPTION_ENDS_SOON":
      return {
        icon: CreditCard,
        iconClassName: "text-foreground",
        titleClassName: "text-foreground",
        unreadDotClassName: "bg-primary",
        toastTone: "default",
      };
    case "UNKNOWN":
    default:
      return getFallbackPresentation(notification);
  }
}

function getFallbackPresentation(
  notification: Pick<NotificationEntity, "type" | "priority">,
): NotificationPresentation {
  if (notification.type === "SYSTEM") {
    if (
      notification.priority === "HIGH" ||
      notification.priority === "URGENT"
    ) {
      return {
        icon: AlertTriangle,
        iconClassName: "text-destructive",
        titleClassName: "text-destructive",
        unreadDotClassName: "bg-destructive",
        toastTone: "destructive",
      };
    }

    return {
      icon: AlertTriangle,
      iconClassName: "text-foreground",
      titleClassName: "text-foreground",
      unreadDotClassName: "bg-primary",
      toastTone: "default",
    };
  }

  return {
    icon: Bell,
    iconClassName: "text-foreground",
    titleClassName: "text-foreground",
    unreadDotClassName: "bg-primary",
    toastTone: "default",
  };
}
